import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiShoppingBag,
  FiUsers,
  FiTruck,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiRefreshCw,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import Chart from "chart.js/auto";
import { Tooltip as ReactTooltip } from "react-tooltip";

// Import Redux actions and selectors
import {
  fetchDashboardStats,
  fetchSalesOverview,
  fetchRecentOrders,
  fetchTopSellingProducts,
} from "../../../store/features/dashbaord/dashboardActions";
import {
  selectSalesOverview,
  selectRecentOrders,
  selectTopSellingProducts,
  selectDashboardLoading,
  selectDashboardError,
  selectTotalRevenue,
  selectTotalSales,
  selectTotalCustomers,
  selectTotalOrders,
} from "../../../store/features/dashbaord/dashboardSelectors";

// Components
const TooltipHorizon = ({ content, placement, trigger }) => {
  return (
    <div className="relative inline-block">
      <div data-tooltip-id="my-tooltip" data-tooltip-content={content}>
        {trigger}
      </div>
      <ReactTooltip
        id="my-tooltip"
        place={placement || "top"}
        effect="solid"
        className="z-50"
      />
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const salesChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const salesChartInstance = useRef(null);
  const revenueChartInstance = useRef(null);

  // Select data from Redux store
  const salesOverview = useSelector(selectSalesOverview);
  const recentOrders = useSelector(selectRecentOrders);
  const topSellingProducts = useSelector(selectTopSellingProducts);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const totalRevenue = useSelector(selectTotalRevenue);
  const totalSales = useSelector(selectTotalSales);
  const totalCustomers = useSelector(selectTotalCustomers);
  const totalOrders = useSelector(selectTotalOrders);

  const [refreshTime, setRefreshTime] = useState(new Date());

  const stats = [
    {
      title: "Total Sales",
      value: totalSales || 0,
      icon: <FiShoppingBag className="text-blue-500" />,
    },
    {
      title: "Total Revenue",
      value: `₹${totalRevenue || 0}`,
      icon: <FaRupeeSign className="text-green-500" />,
    },
    {
      title: "Total Customers",
      value: totalCustomers || 0,
      icon: <FiUsers className="text-purple-500" />,
    },
    {
      title: "Total Orders",
      value: totalOrders || 0,
      icon: <FiTruck className="text-yellow-500" />,
    },
  ];

  // Format month names like "Jan 2025"
  const formatMonth = (dateString) => {
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'short' }) + ' ' + year;
  };

  // Prepare chart data from API
  const salesData = {
    labels: salesOverview.map(item => formatMonth(item.month)),
    datasets: [
      {
        label: "Sales",
        data: salesOverview.map(item => item.totalSales),
        backgroundColor: "rgba(59, 130, 246, 0.5)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
        tension: 0.4,
      },
    ],
  };
  // Prepare doughnut chart data for top products
  const topProductsData = {
    labels: topSellingProducts.map(product => product.productName),
    datasets: [
      {
        data: topSellingProducts.map(product => product.revenue),
        backgroundColor: [
          "rgba(59, 130, 246, 0.7)",
          "rgba(16, 185, 129, 0.7)",
          "rgba(245, 158, 11, 0.7)",
          "rgba(139, 92, 246, 0.7)",
          "rgba(156, 163, 175, 0.7)",
        ],
        borderColor: [
          "rgba(59, 130, 246, 1)",
          "rgba(16, 185, 129, 1)",
          "rgba(245, 158, 11, 1)",
          "rgba(139, 92, 246, 1)",
          "rgba(156, 163, 175, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    // Fetch all dashboard data
    dispatch(fetchDashboardStats());
    dispatch(fetchSalesOverview());
    dispatch(fetchRecentOrders());
    dispatch(fetchTopSellingProducts());
  }, [dispatch]);

  useEffect(() => {
    if (salesOverview.length > 0) {
      // Initialize sales chart
      if (salesChartRef.current) {
        if (salesChartInstance.current) {
          salesChartInstance.current.destroy();
        }
        salesChartInstance.current = new Chart(salesChartRef.current, {
          type: "line",
          data: salesData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              x: {
                ticks: {
                  color: "#6B7280", // Brighter gray for x-axis labels
                },
                grid: {
                  color: "rgba(0, 0, 0, 0.05)",
                },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: "#6B7280", // Brighter gray for y-axis labels
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  color: "rgba(0, 0, 0, 0.05)",
                },
              },
            },
          },
        });
      }

      // Initialize revenue chart
      if (revenueChartRef.current) {
        if (revenueChartInstance.current) {
          revenueChartInstance.current.destroy();
        }
        revenueChartInstance.current = new Chart(revenueChartRef.current, {
          type: "doughnut",
          data: topProductsData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "right",
                labels: {
                  color: "#374151", // Brighter gray for legend text
                },
              },
            },
          },
        });
      }
    }

    return () => {
      if (salesChartInstance.current) {
        salesChartInstance.current.destroy();
      }
      if (revenueChartInstance.current) {
        revenueChartInstance.current.destroy();
      }
    };
  }, [salesOverview, topSellingProducts]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-lg text-gray-500">Loading dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-lg text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Overview</h1>
      <div></div>
      </div>

      {/* Stats Widgets */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center rounded-lg bg-white p-6 shadow"
          >
            <div className="mr-4 rounded-full bg-gray-100 p-3 text-2xl">{stat.icon}</div>
            <div>
              <p className="text-md text-gray-900">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Sales Chart */}
        <div className="rounded-lg bg-white p-6 shadow lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              Sales Overview
            </h2>
            <TooltipHorizon
              content="Monthly sales data"
              placement="top"
              trigger={
                <button className="text-gray-500 hover:text-gray-700">
                  <FiCalendar />
                </button>
              }
            />
          </div>
          <div className="h-64">
            <canvas ref={salesChartRef} />
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              Top Products Revenue
            </h2>
            <TooltipHorizon
              content="Revenue by top selling products"
              placement="top"
              trigger={
                <button className="text-gray-500 hover:text-gray-700">
                  <FiTrendingUp />
                </button>
              }
            />
          </div>
          <div className="h-64">
            <canvas ref={revenueChartRef} />
          </div>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Recent Orders
              </h2>
              <TooltipHorizon
                content={`Last updated: ${refreshTime.toLocaleTimeString()}`}
                placement="top"
                trigger={
                  <button className="text-gray-500 hover:text-gray-700">
                    <FiRefreshCw />
                  </button>
                }
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-500 text-white ">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider ">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider ">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider ">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider ">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {recentOrders.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {order.orderId.slice(0, 8)}...
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {order.customerName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      ₹{order.amount}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Top Selling Products
              </h2>
              <TooltipHorizon
                content="Products by sales quantity and revenue"
                placement="top"
                trigger={
                  <button className="text-gray-500 hover:text-gray-700">
                    <FiAlertCircle />
                  </button>
                }
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
             <thead className="bg-indigo-500 text-white ">
                <tr>
                  <th className="px-6 py-3 text-left text-md font-medium uppercase tracking-wider ">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-md font-medium uppercase tracking-wider ">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-left text-md font-medium uppercase tracking-wider ">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {topSellingProducts.map((product, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {product.productName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {product.salesQuantity}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      ₹{product.revenue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;